/**
 * Upload-first New document (2026-10-19): read a title out of the chosen file,
 * in the browser, with no AI and no new libraries.
 *
 *   Word / Excel / PowerPoint (.docx .xlsx .pptx …): the file's Title property,
 *     else (Word) the first Title/Heading 1 paragraph or first short line,
 *     else (PowerPoint) the first text on slide 1.
 *   PDF: the Title property, else the largest text near the top of page 1.
 *   Anything else (old .doc, videos): the file name, tidied.
 *
 * Office files are zip archives; the small reader below pulls single entries
 * out with file.slice() so a 100 MB file is never read whole.
 */

export type TitleSource = "properties" | "heading" | "first line" | "file name";
export interface Extracted { title: string; source: TitleSource }

const MAX_TITLE = 200;

/** Remove the noise people leave in titles and file names. */
export function cleanTitle(raw: string): string {
  let t = raw
    .replace(/^Microsoft (Word|Excel|PowerPoint) - /i, "")
    .replace(/\.(docx?|docm|dotx|xlsx?|xlsm|pptx?|vsdx?|pdf|odt|ods|odp|mp4|m4v|mov|webm)$/i, "")
    .replace(/[_]+/g, " ")
    .replace(/\b[A-Z]{2,4}-[A-Z]{3}-[A-Z]{2,4}-\d{3}(-R\d+)?\b/g, "") // DocVault numbers
    .replace(/\b(19|20)\d{2}[-.]?\d{2}[-.]?\d{2}\b/g, "")              // 20261019, 2026-10-19
    .replace(/\b\d{6}\b/g, "")                                          // 101926
    .replace(/\b(rev(ision)?\.?\s*\d+|R\d+|v\d+(\.\d+)*)\b/gi, "")
    .replace(/\(\s*(draft|final|copy|\d+)\s*\)|\b(draft|final|copy)\b$/gi, "")
    .replace(/\s+-\s+(?=\s|$)/g, " ")
    .replace(/\s{2,}/g, " ")
    .replace(/^[\s\-–—:·,.]+|[\s\-–—:·,]+$/g, "")
    .trim();
  // A file name with dashes for spaces: "remote-work-policy".
  if (!/\s/.test(t) && /-/.test(t)) t = t.replace(/-+/g, " ");
  // All-lowercase file names read better in title case.
  if (t && t === t.toLowerCase()) t = t.replace(/\b([a-z])/g, (m) => m.toUpperCase());
  return t.slice(0, MAX_TITLE);
}

/** Titles that are just a program's placeholder. */
function usable(t: string | null | undefined): t is string {
  if (!t) return false;
  const s = t.trim();
  return s.length >= 3 && s.length <= MAX_TITLE &&
    !/^(untitled|document\s*\d*|doc\d*|book\d*|presentation\d*|slide\s*\d*|title|microsoft (word|excel|powerpoint).*|powerpoint presentation|pdf|new document)$/i.test(s);
}

// ------------------------------------------------------------------ zip ---

interface ZipEntry { name: string; method: number; compSize: number; offset: number }

async function bytesOf(blob: Blob): Promise<Uint8Array> {
  return new Uint8Array(await blob.arrayBuffer());
}
const u16 = (b: Uint8Array, o: number) => b[o]! | (b[o + 1]! << 8);
const u32 = (b: Uint8Array, o: number) => (b[o]! | (b[o + 1]! << 8) | (b[o + 2]! << 16) | (b[o + 3]! << 24)) >>> 0;

async function zipEntries(file: File): Promise<ZipEntry[] | null> {
  const tailLen = Math.min(file.size, 65557);
  const tail = await bytesOf(file.slice(file.size - tailLen));
  let eocd = -1;
  for (let i = tail.length - 22; i >= 0; i--) if (u32(tail, i) === 0x06054b50) { eocd = i; break; }
  if (eocd < 0) return null;
  const count = u16(tail, eocd + 10), cdSize = u32(tail, eocd + 12), cdOff = u32(tail, eocd + 16);
  if (cdOff + cdSize > file.size) return null;
  const cd = await bytesOf(file.slice(cdOff, cdOff + cdSize));
  const out: ZipEntry[] = [];
  const dec = new TextDecoder();
  for (let p = 0, n = 0; n < count && p + 46 <= cd.length; n++) {
    if (u32(cd, p) !== 0x02014b50) break;
    const nameLen = u16(cd, p + 28), extraLen = u16(cd, p + 30), commentLen = u16(cd, p + 32);
    out.push({ name: dec.decode(cd.subarray(p + 46, p + 46 + nameLen)), method: u16(cd, p + 10), compSize: u32(cd, p + 20), offset: u32(cd, p + 42) });
    p += 46 + nameLen + extraLen + commentLen;
  }
  return out;
}

async function zipText(file: File, entries: ZipEntry[], name: string, maxBytes = 4_000_000): Promise<string | null> {
  const e = entries.find((x) => x.name === name);
  if (!e || e.compSize > maxBytes) return null;
  const head = await bytesOf(file.slice(e.offset, e.offset + 30));
  if (u32(head, 0) !== 0x04034b50) return null;
  const start = e.offset + 30 + u16(head, 26) + u16(head, 28);
  const raw = file.slice(start, start + e.compSize);
  if (e.method === 0) return new TextDecoder().decode(await bytesOf(raw));
  if (e.method !== 8 || typeof DecompressionStream === "undefined") return null;
  const stream = raw.stream().pipeThrough(new DecompressionStream("deflate-raw"));
  return new Response(stream).text();
}

const xmlUnescape = (s: string) => s
  .replace(/&lt;/g, "<").replace(/&gt;/g, ">").replace(/&quot;/g, '"').replace(/&apos;/g, "'")
  .replace(/&#(\d+);/g, (_, n) => String.fromCodePoint(Number(n)))
  .replace(/&#x([0-9a-f]+);/gi, (_, n) => String.fromCodePoint(parseInt(n, 16)))
  .replace(/&amp;/g, "&");

/** Text of one <w:p> paragraph (all <w:t> runs). */
const wordParaText = (p: string) => xmlUnescape([...p.matchAll(/<w:t(?:\s[^>]*)?>([^<]*)<\/w:t>/g)].map((m) => m[1]).join("")).replace(/\s+/g, " ").trim();

async function fromOffice(file: File): Promise<Extracted | null> {
  const entries = await zipEntries(file);
  if (!entries) return null;
  const core = await zipText(file, entries, "docProps/core.xml");
  const prop = core?.match(/<dc:title[^>]*>([^<]*)<\/dc:title>/)?.[1];
  if (prop && usable(cleanTitle(xmlUnescape(prop)))) return { title: cleanTitle(xmlUnescape(prop)), source: "properties" };

  const doc = await zipText(file, entries, "word/document.xml", 20_000_000);
  if (doc) {
    const paras = [...doc.slice(0, 400_000).matchAll(/<w:p[ >][\s\S]*?<\/w:p>/g)].map((m) => m[0]).slice(0, 80);
    const styled = paras.find((p) => /<w:pStyle w:val="(Title|Heading1|Heading 1|1)"/i.test(p) && usable(wordParaText(p)));
    if (styled) return { title: cleanTitle(wordParaText(styled)), source: "heading" };
    const first = paras.map(wordParaText).find((t) => usable(t) && t.length <= 120);
    if (first && usable(cleanTitle(first))) return { title: cleanTitle(first), source: "first line" };
  }
  const slide = await zipText(file, entries, "ppt/slides/slide1.xml");
  if (slide) {
    const t = xmlUnescape([...slide.matchAll(/<a:t>([^<]*)<\/a:t>/g)].map((m) => m[1]).find((x) => usable(x)) ?? "");
    if (usable(cleanTitle(t))) return { title: cleanTitle(t), source: "heading" };
  }
  return null;
}

// ------------------------------------------------------------------ pdf ---

async function fromPdf(file: File): Promise<Extracted | null> {
  const pdfjs = await import("pdfjs-dist");
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    const w = await import("pdfjs-dist/build/pdf.worker.min.mjs?url");
    pdfjs.GlobalWorkerOptions.workerSrc = w.default;
  }
  const doc = await pdfjs.getDocument({ data: await file.arrayBuffer() }).promise;
  try {
    const meta = await doc.getMetadata().catch(() => null);
    const info = (meta?.info ?? {}) as { Title?: string };
    if (usable(cleanTitle(info.Title ?? ""))) return { title: cleanTitle(info.Title!), source: "properties" };
    const page = await doc.getPage(1);
    const h = page.view[3] ?? 792;
    type TextBit = { str?: string; transform?: number[] };
    const items = ((await page.getTextContent()).items as TextBit[])
      .filter((i) => !!i.str?.trim() && Array.isArray(i.transform))
      .map((i) => { const tr = i.transform!; return { s: i.str!, size: Math.round(Math.hypot(tr[2] ?? 0, tr[3] ?? 0)), y: tr[5] ?? 0 }; })
      .filter((i) => i.y > h * 0.35); // top two-thirds of the page
    if (!items.length) return null;
    const big = Math.max(...items.map((i) => i.size));
    const line = items.filter((i) => i.size === big).map((i) => i.s).join(" ").replace(/\s+/g, " ").trim();
    return usable(cleanTitle(line)) && line.length <= 160 ? { title: cleanTitle(line), source: "heading" } : null;
  } finally {
    void doc.destroy();
  }
}

/** Best title for a chosen file. Never throws; falls back to the file name. */
export async function extractTitle(file: File): Promise<Extracted> {
  const name = file.name.toLowerCase();
  try {
    const found = /\.(docx|docm|dotx|xlsx|xlsm|pptx|vsdx)$/.test(name) ? await fromOffice(file)
      : /\.pdf$/.test(name) || file.type === "application/pdf" ? await fromPdf(file)
      : null;
    if (found) return found;
  } catch {
    /* unreadable or protected: use the file name */
  }
  return { title: cleanTitle(file.name), source: "file name" };
}

/** Suggest a document type from words in the title or file name (ADR-0012 types). */
export function guessType(...texts: string[]): string | null {
  const s = " " + texts.join(" ").toLowerCase().replace(/[_\-]+/g, " ") + " ";
  // ADR-0016 code lists.
  const rules: [RegExp, string][] = [
    [/\bsop\b|standard operating/, "SOP"],
    [/\btemplate\b/, "TMP"],
    [/\bchecklist\b/, "CHK"],
    [/\bguidelines?\b|best practices?/, "GUI"],
    [/\bknowledge ?base\b|\bkb\b|troubleshoot/, "KB"],
    [/\bhow ?to\b|quick ?start|\bwalkthrough\b|\btutorial\b/, "HOW"],
    [/\bforms?\b/, "FRM"],
    [/\bplan\b|\bplaybook\b|continuity|disaster recovery|\bbcp\b|\bdrp\b|\birp\b/, "PLN"],
    [/\bprocedures?\b/, "SOP"],
    [/\bpolicy\b|\bpolicies\b/, "POL"],
    [/\bspecification\b|\bspec\b/, "SPEC"],
    [/\barchitecture\b/, "ARCH"],
    [/\bdiagram\b|\btopology\b/, "DIA"],
    [/\bconfiguration\b|\bconfig\b/, "CFG"],
    [/\bassessment\b/, "ASM"],
    [/\baudit\b/, "AUD"],
    [/\bregister\b/, "REG"],
    [/\breport\b/, "RPT"],
    [/\bmanual\b|\bhandbook\b/, "MAN"],
    [/\bagreement\b|\bnda\b/, "AGR"],
    [/\bcontract\b/, "CON"],
    [/\bstandards?\b|\bbaseline\b/, "STD"],
    [/\bprocess\b|\bworkflow\b/, "PRO"],
    [/\brequest\b|\bapplication\b/, "FRM"],
    [/\bguide\b/, "HOW"],
  ];
  for (const [re, t] of rules) if (re.test(s)) return t;
  return null;
}
